import { useEffect, useMemo, useState } from "react";
import { fetchModelsByBrand, deleteModel, updateModel } from "../../api/models";
import AddModelModal from "./AddModelModal";

function ActionIconButton({
  title,
  onClick,
  disabled = false,
  loading = false,
  tone = "default",
  children,
}) {
  const toneClass =
    tone === "danger"
      ? "text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40"
      : "text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white";

  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      className={`flex h-10 w-10 items-center justify-center rounded-xl transition disabled:cursor-not-allowed disabled:opacity-50 ${toneClass}`}
    >
      {loading ? (
        <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      ) : (
        children
      )}
    </button>
  );
}

function ModalShell({ open, icon, title, description, children }) {
  if (!open) return null;

  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/45 px-4 backdrop-blur-sm dark:bg-black/65">
      <div
        className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-800 dark:bg-slate-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-2xl dark:bg-slate-800">
            {icon}
          </div>
        </div>

        <h3 className="text-center text-lg font-semibold text-slate-900 dark:text-white">
          {title}
        </h3>

        {description && (
          <p className="mt-2 text-center text-sm leading-6 text-slate-500 dark:text-slate-400">
            {description}
          </p>
        )}

        <div className="mt-6">{children}</div>
      </div>
    </div>
  );
}

function EmptyState({ search }) {
  return (
    <div className="flex h-full min-h-[260px] flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 px-4 text-center dark:border-slate-800 dark:bg-slate-950/20">
      <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-2xl dark:bg-slate-800">
        🚜
      </div>

      <h4 className="text-sm font-semibold text-slate-900 dark:text-white">
        No models found
      </h4>

      <p className="mt-2 max-w-xs text-sm leading-6 text-slate-500 dark:text-slate-400">
        {search.trim()
          ? "No models match your current search."
          : "There are no models under this brand yet. Add one to start building the catalog."}
      </p>
    </div>
  );
}

export default function ModelsList({ brand }) {
  const [models, setModels] = useState([]);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(false);

  const [deletingModelId, setDeletingModelId] = useState(null);
  const [modelToDelete, setModelToDelete] = useState(null);

  const [editingModelId, setEditingModelId] = useState(null);
  const [modelToEdit, setModelToEdit] = useState(null);
  const [editName, setEditName] = useState("");
  const [editError, setEditError] = useState("");

  useEffect(() => {
    if (!brand?.id) return;

    let active = true;

    const loadModels = async () => {
      try {
        setLoading(true);
        const data = await fetchModelsByBrand({ brandId: brand.id });
        if (!active) return;
        setModels(Array.isArray(data) ? data : []);
      } finally {
        if (active) setLoading(false);
      }
    };

    loadModels();

    return () => {
      active = false;
    };
  }, [brand]);

  const refreshModels = async () => {
    if (!brand?.id) return;

    try {
      setLoading(true);
      const data = await fetchModelsByBrand({ brandId: brand.id });
      setModels(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  };

  const filteredModels = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return models;

    return models.filter((model) =>
      String(model.name || "").toLowerCase().includes(q)
    );
  }, [models, search]);

  const openDeleteModal = (model) => {
    setModelToDelete(model);
  };

  const closeDeleteModal = () => {
    if (deletingModelId) return;
    setModelToDelete(null);
  };

  const confirmDeleteModel = async () => {
    if (!brand?.id || !modelToDelete?.id) return;

    try {
      setDeletingModelId(modelToDelete.id);

      await deleteModel({
        brandId: brand.id,
        modelId: modelToDelete.id,
      });

      setModels((prev) => prev.filter((m) => m.id !== modelToDelete.id));
      setModelToDelete(null);
    } catch (error) {
      alert(error.message || "Failed to delete model");
    } finally {
      setDeletingModelId(null);
    }
  };

  const openEditModal = (model) => {
    setModelToEdit(model);
    setEditName(model.name || "");
    setEditError("");
  };

  const closeEditModal = () => {
    if (editingModelId) return;
    setModelToEdit(null);
    setEditName("");
    setEditError("");
  };

  const confirmEditModel = async () => {
    if (!modelToEdit?.id) return;

    const trimmed = editName.trim();

    if (!trimmed) {
      setEditError("Model name is required");
      return;
    }

    try {
      setEditingModelId(modelToEdit.id);
      setEditError("");

      await updateModel({
        modelId: modelToEdit.id,
        name: trimmed,
      });

      setModels((prev) =>
        prev.map((model) =>
          model.id === modelToEdit.id ? { ...model, name: trimmed } : model
        )
      );

      setModelToEdit(null);
      setEditName("");
    } catch (error) {
      setEditError(error.message || "Failed to update model");
    } finally {
      setEditingModelId(null);
    }
  };

  return (
    <div className="relative flex h-full flex-col text-slate-900 dark:text-slate-100">
      {/* Toolbar */}
      <div className="space-y-4 border-b border-slate-200 pb-5 dark:border-slate-800">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
              Model Catalog
            </p>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {models.length} registered model{models.length === 1 ? "" : "s"} under{" "}
              <span className="font-medium text-slate-900 dark:text-white">
                {brand?.name}
              </span>
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={refreshModels}
              className="inline-flex items-center justify-center rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              Refresh
            </button>

            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-black px-5 py-3 text-sm font-semibold text-white transition hover:opacity-90 dark:bg-white dark:text-black"
            >
              <span className="text-base leading-none">＋</span>
              Add Model
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 xl:grid-cols-[1fr_auto]">
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500">
              🔎
            </span>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search models..."
              className="w-full rounded-2xl border border-slate-300 bg-white py-3 pl-10 pr-4 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-900 focus:ring-4 focus:ring-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:placeholder:text-slate-500 dark:focus:border-white dark:focus:ring-slate-800"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <div className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              Brand: {brand?.name}
            </div>
            <div className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              Results: {filteredModels.length}
            </div>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto py-5">
        {loading ? (
          <div className="flex h-full min-h-[260px] flex-col items-center justify-center text-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-300 border-t-slate-700 dark:border-slate-700 dark:border-t-slate-200" />
            <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
              Loading models...
            </p>
          </div>
        ) : filteredModels.length === 0 ? (
          <EmptyState search={search} />
        ) : (
          <div className="space-y-3">
            {filteredModels.map((model, index) => {
              const isDeleting = deletingModelId === model.id;
              const isEditing = editingModelId === model.id;

              return (
                <div
                  key={model.id}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-4 transition hover:border-slate-300 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-slate-700 dark:hover:bg-slate-800/60"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-sm font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                          {index + 1}
                        </div>

                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">
                            {model.name}
                          </p>
                          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                            Registered under {brand?.name}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-1">
                      <ActionIconButton
                        title={`Edit ${model.name}`}
                        onClick={() => openEditModal(model)}
                        disabled={isDeleting || isEditing}
                        loading={isEditing}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="h-4 w-4"
                        >
                          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm17.71-10.04a1.003 1.003 0 0 0 0-1.42l-2.5-2.5a1.003 1.003 0 0 0-1.42 0l-1.96 1.96 3.75 3.75 2.13-1.79z" />
                        </svg>
                      </ActionIconButton>

                      <ActionIconButton
                        title={`Delete ${model.name}`}
                        onClick={() => openDeleteModal(model)}
                        disabled={isDeleting || isEditing}
                        loading={isDeleting}
                        tone="danger"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="h-4 w-4"
                        >
                          <path d="M6 7h12l-1 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L6 7zm3-4h6l1 2h4v2H4V5h4l1-2z" />
                        </svg>
                      </ActionIconButton>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showAdd && (
        <AddModelModal
          brandId={brand.id}
          brandName={brand.name}
          onClose={() => {
            setShowAdd(false);
            refreshModels();
          }}
        />
      )}

      <ModalShell
        open={!!modelToEdit}
        icon="✏️"
        title="Edit Model"
        description={
          modelToEdit ? `Update the name for ${modelToEdit.name}.` : ""
        }
      >
        <div className="space-y-5">
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Model name
            </label>
            <input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Enter model name"
              disabled={!!editingModelId}
              className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-900 focus:ring-4 focus:ring-slate-200 disabled:opacity-70 dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:placeholder:text-slate-500 dark:focus:border-white dark:focus:ring-slate-800"
            />
            {editError && (
              <p className="mt-2 text-sm text-red-500 dark:text-red-400">
                {editError}
              </p>
            )}
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={closeEditModal}
              disabled={!!editingModelId}
              className="flex-1 rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={confirmEditModel}
              disabled={!!editingModelId}
              className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-black px-4 py-3 text-sm font-medium text-white transition hover:opacity-90 disabled:opacity-70 dark:bg-white dark:text-black"
            >
              {editingModelId === modelToEdit?.id ? (
                <>
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent dark:border-black" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </div>
      </ModalShell>

      <ModalShell
        open={!!modelToDelete}
        icon="🗑️"
        title="Delete Model?"
        description={
          modelToDelete
            ? `You are about to delete ${modelToDelete.name} from ${brand?.name}. This action cannot be undone.`
            : ""
        }
      >
        <div className="flex gap-3">
          <button
            type="button"
            onClick={closeDeleteModal}
            disabled={!!deletingModelId}
            className="flex-1 rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={confirmDeleteModel}
            disabled={!!deletingModelId}
            className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-red-600 px-4 py-3 text-sm font-medium text-white transition hover:bg-red-700 disabled:opacity-70"
          >
            {deletingModelId === modelToDelete?.id ? (
              <>
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Deleting...
              </>
            ) : (
              "Delete Model"
            )}
          </button>
        </div>
      </ModalShell>
    </div>
  );
}